import { AxiosResponse } from "axios";
import { IOptions } from "../../api-sdk";
import BaseService from "../baseService";
import {
  ISubscriptionQuery,
  ISubscriptionQueryV2,
  ISubscriptions,
  ISubscriptionUpdate,
  ISubscriptionUpdateV2,
  ISubscriptionV2,
} from "./subscription.types";
import qs from "qs";
import { filterNonNull } from "../../../utils";
import { IPaginatedResponse } from "../../../types";

class SubscriptionService extends BaseService {
  constructor(options: IOptions) {
    super(options);
  }

  /**
   * Initializes a new subscription on the server.
   *
   * @deprecated Use create() instead.
   * @returns A promise that resolves to the server's response containing the ID of the new subscription.
   */
  async initNew(): Promise<AxiosResponse<{ id: string }>> {
    return this.request({
      method: "POST",
      url: "/api/v1/subscriptions",
    });
  }

  /**
   * Creates a new subscription on the server.
   *
   * @returns A promise that resolves to the server's response containing the ID of the new subscription.
   */
  async create(): Promise<AxiosResponse<{ id: string }>> {
    return this.request({
      method: "POST",
      url: "/api/v2/subscriptions",
    });
  }

  /**
   * Deletes a subscription by its ID.
   *
   * @deprecated Use deleteV2() instead.
   * @param id The ID of the subscription to delete.
   * @returns A promise that resolves to the server's response containing the ID of the deleted subscription.
   */
  async delete(id: string): Promise<AxiosResponse<{ id: string }>> {
    return this.request({
      method: "DELETE",
      url: `/api/v1/subscriptions/${id}`,
    });
  }

  /**
   * Deletes a subscription by its ID.
   *
   * @param id The ID of the subscription to delete.
   * @returns A promise that resolves to the server's response containing the message of the deleted subscription.
   */
  async deleteV2(id: string): Promise<AxiosResponse<{ message: string }>> {
    return this.request({
      method: "DELETE",
      url: `/api/v2/subscriptions/${id}`,
    });
  }

  /**
   * Updates an existing subscription on the server.
   *
   * @deprecated Use updateV2() instead.
   * @param id The ID of the subscription to update.
   * @param payload The subscription data to update on the server.
   * @returns A promise that resolves to the server's response containing the ID of the updated subscription.
   */
  async update(
    id: string,
    payload: ISubscriptionUpdate
  ): Promise<AxiosResponse<{ id: string }>> {
    return this.request({
      method: "PUT",
      url: `/api/v1/subscriptions/${id}`,
      data: payload,
    });
  }

  /**
   * Updates an existing subscription on the server.
   *
   * @param id The ID of the subscription to update.
   * @param payload The subscription data to update on the server.
   * @returns A promise that resolves to the server's response containing the ID of the updated subscription.
   */
  async updateV2(
    id: string,
    payload: ISubscriptionUpdateV2
  ): Promise<AxiosResponse<{ id: string }>> {
    return this.request({
      method: "PUT",
      url: `/api/v2/subscriptions/${id}`,
      data: payload,
    });
  }

  /**
   * Fetches the list of subscriptions.
   *
   * @deprecated Use getV2() instead.
   * @returns A promise that resolves to the server's response containing the list of subscriptions.
   */
  async get(): Promise<AxiosResponse<ISubscriptions>> {
    return this.request({
      method: "GET",
      url: "/api/subscriptions",
    });
  }

  /**
   * Fetches the list of subscriptions.
   *
   * @param query Optional query parameters to filter the results.
   * @returns A promise that resolves to the server's response containing the list of subscriptions.
   */
  async getV2(
    query?: ISubscriptionQueryV2
  ): Promise<AxiosResponse<IPaginatedResponse<ISubscriptionV2>>> {
    return this.request({
      method: "GET",
      url: `/api/v2/subscriptions?${qs.stringify(filterNonNull(query || {}))}`,
    });
  }

  /**
   * Retrieves a list of subscriptions for management.
   *
   * @deprecated Use getManageV2() instead.
   * @param query Optional query parameters to filter the results.
   * The available query parameters are:
   * - `page`: The page number to retrieve.
   * - `limit`: The number of items to retrieve per page.
   * - `order`: The order to sort the results by. The available values are `asc` and `desc`.
   * - `fields`: An array of column names to include in the results.
   * - `q`: A search query to filter the results by.
   * - `filters`: An object of filter values to filter the results by.
   * - `populate`: An array of populate options to include associated data in the results.
   * @returns A promise that resolves to the server's response containing the list of subscriptions for management.
   */
  async getManage(
    query?: ISubscriptionQuery
  ): Promise<AxiosResponse<ISubscriptions>> {
    return this.request({
      method: "GET",
      url:
        "/api/v1/subscriptions/manage" +
        (query ? `?${qs.stringify(filterNonNull(query))}` : ""),
    });
  }

  /**
   * Retrieves a list of subscriptions for management.
   *
   * @param query Optional query parameters to filter the results.
   * @returns A promise that resolves to the server's response containing the list of subscriptions for management.
   */
  async getManageV2(
    query?: ISubscriptionQueryV2
  ): Promise<AxiosResponse<IPaginatedResponse<ISubscriptionV2>>> {
    return this.request({
      method: "GET",
      url: `/api/v2/subscriptions/manage?${qs.stringify(
        filterNonNull(query || {})
      )}`,
    });
  }
}

export default SubscriptionService;
